import { FC, useState } from 'react';
import { VisibilityOutlined } from '@mui/icons-material';
import { Box, Button, Checkbox, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { FileModel } from 'api';
import InformationRequestExpirationDatePicker from 'dashboard409a/components/InformationRequestExpirationDatePicker/InformationRequestExpirationDatePicker';
import MarkDocumentNaDialog from '../MarkDocumentNaDialog';
import OnboardingEmailDocumentPreviewDialog from '../OnboardingEmailDocumentPreviewDialog';
import { getOnboardingFulfillmentSuffix } from '../OnboardingEmailModal.fulfillment';
import type { DocumentChecklistItem } from '../OnboardingEmailModal.types';
import { useOnboardingEmailModalContext } from '../OnboardingEmailModalContext';

const OnboardingEmailInputsPanel: FC = () => {
  const {
    checklist,
    toggleDocument,
    markDocumentAsNa,
    isMarkingDocumentNa,
    requestDocumentAgain,
    isRestoringDocument,
    isLoadingDocuments,
    requestedDocuments,
    generateEmail,
    isGenerating,
    canGenerate,
    isFollowUp,
    expirationDate,
    setExpirationDate,
  } = useOnboardingEmailModalContext();

  const [previewFile, setPreviewFile] = useState<FileModel | null>(null);
  const [documentToMarkNa, setDocumentToMarkNa] = useState<DocumentChecklistItem | null>(null);

  const requestCountLabel = (() => {
    if (requestedDocuments.length > 0) {
      const countLabel = `${requestedDocuments.length} item${
        requestedDocuments.length === 1 ? '' : 's'
      } will be requested`;
      return isFollowUp
        ? `${countLabel}. Provided documents and answered questions are excluded from the follow-up.`
        : countLabel;
    }
    if (isFollowUp) {
      return 'All selected items are already provided — regenerate to draft a follow-up.';
    }
    return 'Select documents that still need to be requested';
  })();

  const generateButtonLabel = (() => {
    if (isGenerating) {
      return 'Generating…';
    }
    return isFollowUp ? 'Regenerate follow-up email' : 'Generate onboarding email';
  })();

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {requestCountLabel}
      </Typography>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Request expiration date
        </Typography>
        <InformationRequestExpirationDatePicker
          value={expirationDate}
          onChange={setExpirationDate}
          disabled={isGenerating}
          id="onboarding-email-expiration-date"
        />
      </Box>
      <Button
        variant="contained"
        onClick={() => generateEmail()}
        disabled={!canGenerate}
        id="generate-onboarding-email-button"
      >
        {generateButtonLabel}
      </Button>
      {isGenerating && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main' }}>
          <CircularProgress size={16} />
          <Typography variant="caption">
            {isFollowUp
              ? 'Updating document request and drafting follow-up email…'
              : 'Creating document request and drafting email…'}
          </Typography>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Documents and questions to request
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Completed items are grayed out and disabled. Uploaded documents can be previewed with the
          eyeball icon; N/A documents can be marked with the N/A button. Answers appear under each
          question. Unchecked items already on the portal are marked N/A when you generate; use
          Request again to put one back on the portal.
        </Typography>
        {isLoadingDocuments ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption">Checking uploaded documents and answers…</Typography>
          </Box>
        ) : (
          <Stack spacing={0.5}>
            {checklist.map(item => {
              const uploadedFile = item.uploadedFiles?.[0] ?? null;
              const isQuestion = item.kind === 'question';
              const hasAnswer = Boolean(item.answerDisplay?.trim());
              const childQuestions = item.childQuestions ?? [];
              const isComplete = item.alreadyHave;
              const completeSuffix = isQuestion
                ? ' (answered)'
                : getOnboardingFulfillmentSuffix(
                  item.fulfillmentStatus,
                  Boolean(uploadedFile)
                );
              const slug = item.label.replace(/\s+/g, '-').toLowerCase();
              const isMarkedNa =
                item.fulfillmentStatus === 'not_applicable'
                || item.fulfillmentStatus === 'not_available';
              // Completed documents are previewable: swap the checkbox for the
              // eyeball icon. Completed questions keep a disabled checkbox.
              const showPreviewInsteadOfCheckbox =
                isComplete && !isQuestion && Boolean(uploadedFile) && !isMarkedNa;
              const showMarkNaButton = !isQuestion && !isComplete;
              const showRequestAgainButton = isMarkedNa && !uploadedFile && Boolean(item.requestedFileId);
              return (
                <Box key={`${item.kind}-${item.name}`}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    {showPreviewInsteadOfCheckbox ? (
                      <Tooltip title="Preview uploaded document">
                        <IconButton
                          size="small"
                          onClick={() => setPreviewFile(uploadedFile)}
                          aria-label={`Preview ${item.label}`}
                          id={`onboarding-doc-preview-${slug}`}
                          data-testid="onboarding-doc-preview-button"
                          sx={{ p: 0.75 }}>
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Checkbox
                        checked={item.checked}
                        disabled={isComplete}
                        onChange={() => toggleDocument(item.name)}
                        id={`onboarding-doc-${slug}`}
                        size="small"
                        sx={{ pt: 0.25 }}
                      />
                    )}
                    <Box sx={{ flex: 1, pt: 0.5 }}>
                      <Typography
                        variant="body2"
                        color={isComplete ? 'text.disabled' : undefined}>
                        {item.label}
                        {isComplete ? completeSuffix : ''}
                      </Typography>
                      {isQuestion && hasAnswer && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ display: 'block', mt: 0.25 }}
                          data-testid="onboarding-question-answer">
                          Answer: {item.answerDisplay}
                        </Typography>
                      )}
                      {isQuestion &&
                        childQuestions.map(child => (
                          <Typography
                            key={child.label}
                            variant="caption"
                            color={child.answered ? 'text.disabled' : 'text.secondary'}
                            sx={{ display: 'block', mt: 0.25, pl: 1 }}
                            data-testid="onboarding-question-child-answer">
                            {child.label}
                            {child.answerDisplay ? `: ${child.answerDisplay}` : ''}
                          </Typography>
                        ))}
                      {!isQuestion && isMarkedNa && item.fulfillmentNote ? (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ display: 'block', mt: 0.25 }}
                          data-testid="onboarding-document-na-note">
                          Note: {item.fulfillmentNote}
                        </Typography>
                      ) : null}
                    </Box>
                    {showMarkNaButton ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setDocumentToMarkNa(item)}
                        disabled={isMarkingDocumentNa}
                        id={`onboarding-doc-mark-na-${slug}`}
                        data-testid="onboarding-doc-mark-na-button"
                        sx={{ minWidth: 0, px: 1, mt: 0.25 }}
                      >
                        N/A
                      </Button>
                    ) : null}
                    {showRequestAgainButton ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => requestDocumentAgain(item)}
                        disabled={isRestoringDocument}
                        id={`onboarding-doc-request-again-${slug}`}
                        data-testid="onboarding-doc-request-again-button"
                        sx={{ minWidth: 0, px: 1, mt: 0.25 }}
                      >
                        Request again
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
      <OnboardingEmailDocumentPreviewDialog
        open={Boolean(previewFile)}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
      <MarkDocumentNaDialog
        item={documentToMarkNa}
        isSubmitting={isMarkingDocumentNa}
        onClose={() => setDocumentToMarkNa(null)}
        onConfirm={async (item, status, note) => {
          await markDocumentAsNa(item, status, note);
          setDocumentToMarkNa(null);
        }}
      />
    </Stack>
  );
};

export default OnboardingEmailInputsPanel;
